import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { employeeApi, getEmployeeData } from '../../lib/employeeApi'
import { ChevronLeft, Send, Camera, X } from 'lucide-react'
import { Capacitor } from '@capacitor/core'

interface Message {
  id: string
  senderId: string
  senderName: string
  recipientId: string
  content: string | null
  imageData: string | null
  isRead: boolean
  createdAt: string
}

function processImage(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    img.onload = () => {
      const MAX = 900
      let w = img.naturalWidth, h = img.naturalHeight
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX }
        else { w = Math.round(w * MAX / h); h = MAX }
      }
      const canvas = document.createElement('canvas')
      canvas.width = w; canvas.height = h
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
      resolve(canvas.toDataURL('image/jpeg', 0.75).split(',')[1])
    }
    img.onerror = reject
    img.src = src.startsWith('data:') ? src : `data:image/jpeg;base64,${src}`
  })
}

function formatTime(iso: string) {
  const d = new Date(iso)
  const today = new Date()
  const isToday = d.toDateString() === today.toDateString()
  if (isToday) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export default function MobileMessageThread() {
  const { partnerId } = useParams<{ partnerId: string }>()
  const navigate = useNavigate()
  const emp = getEmployeeData()
  const myId = emp?.id

  const [messages, setMessages] = useState<Message[]>([])
  const [partnerName, setPartnerName] = useState('')
  const [text, setText] = useState('')
  const [pendingImage, setPendingImage] = useState<string | null>(null) // base64 à envoyer
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const bottomRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const load = async (silent = false) => {
    if (!partnerId) return
    try {
      const r = await employeeApi.get(`/employee/me/messages/${partnerId}`)
      setMessages(r.data || [])
      if (r.data?.length > 0) {
        const last = r.data[r.data.length - 1]
        const name = last.senderId === partnerId ? last.senderName : last.recipientName
        if (name) setPartnerName(name)
      }
    } catch {} finally { if (!silent) setLoading(false) }
  }

  // Charger le nom du partenaire depuis la liste d'employés si pas de messages
  useEffect(() => {
    employeeApi.get('/employee/me/messages/employees').then(r => {
      const found = (r.data || []).find((e: any) => e.id === partnerId)
      if (found) setPartnerName(`${found.firstName}${found.lastName && found.lastName !== '-' ? ' ' + found.lastName : ''}`)
    }).catch(() => {})
  }, [partnerId])

  useEffect(() => {
    load()
    const interval = setInterval(() => load(true), 5000)
    return () => clearInterval(interval)
  }, [partnerId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const pickPhoto = async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        const { Camera: CapCamera, CameraResultType, CameraSource } = await import('@capacitor/camera')
        const photo = await CapCamera.getPhoto({ quality: 70, allowEditing: false, resultType: CameraResultType.Base64, source: CameraSource.Camera })
        if (photo.base64String) {
          const processed = await processImage(photo.base64String)
          setPendingImage(processed)
        }
      } catch (e: any) { if (!String(e).includes('cancelled')) alert('Impossible d\'accéder à la caméra') }
    } else {
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      try {
        const processed = await processImage(ev.target?.result as string)
        setPendingImage(processed)
      } catch {}
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const send = async () => {
    if ((!text.trim() && !pendingImage) || !partnerId || sending) return
    setSending(true)
    try {
      await employeeApi.post('/employee/me/messages', {
        recipientId: partnerId,
        content: text.trim() || null,
        imageData: pendingImage || null
      })
      setText('')
      setPendingImage(null)
      await load(true)
    } catch { alert('Erreur lors de l\'envoi') }
    finally { setSending(false) }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
  }

  return (
    <div className="flex flex-col bg-gray-100"
      style={{ height: '100dvh' }}>

      {/* Header */}
      <div className="bg-blue-600 text-white px-4 flex items-center gap-3 flex-shrink-0 shadow-md"
        style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}>
        <button onClick={() => navigate('/app/messages')} className="p-1">
          <ChevronLeft size={24} />
        </button>
        <div className="w-9 h-9 rounded-full bg-blue-400 flex items-center justify-center font-bold text-sm flex-shrink-0">
          {partnerName.split(' ').map(n => n[0]).slice(0, 2).join('')}
        </div>
        <span className="font-semibold text-base">{partnerName || '...'}</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {loading ? (
          <div className="text-center text-gray-400 text-sm pt-8">Chargement...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 text-sm pt-8">Aucun message — commencez la conversation !</div>
        ) : (
          messages.map((msg, i) => {
            const isMine = msg.senderId === myId
            const showTime = i === 0 || new Date(msg.createdAt).getTime() - new Date(messages[i - 1].createdAt).getTime() > 5 * 60 * 1000
            return (
              <div key={msg.id}>
                {showTime && (
                  <div className="text-center text-xs text-gray-400 my-2">{formatTime(msg.createdAt)}</div>
                )}
                <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 ${isMine
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'}`}>
                    {msg.imageData && (
                      <img src={`data:image/jpeg;base64,${msg.imageData}`}
                        alt="photo"
                        className="rounded-xl max-w-full mb-1.5 max-h-64 object-cover" />
                    )}
                    {msg.content && (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Aperçu image en attente */}
      {pendingImage && (
        <div className="bg-white border-t px-4 py-2 flex items-center gap-3 flex-shrink-0">
          <div className="relative">
            <img src={`data:image/jpeg;base64,${pendingImage}`} alt="preview"
              className="h-16 w-16 object-cover rounded-xl" />
            <button onClick={() => setPendingImage(null)}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-700 rounded-full flex items-center justify-center">
              <X size={11} className="text-white" />
            </button>
          </div>
          <span className="text-xs text-gray-500">Photo jointe</span>
        </div>
      )}

      {/* Zone de saisie */}
      <div className="bg-white border-t px-3 py-2 flex items-end gap-2 flex-shrink-0"
        style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom))' }}>
        <button onClick={pickPhoto}
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 hover:bg-gray-200 transition-colors mb-0.5">
          <Camera size={20} className="text-gray-500" />
        </button>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message..."
          rows={1}
          className="flex-1 border rounded-2xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-blue-400 bg-gray-50 max-h-28"
          style={{ lineHeight: '1.4' }}
        />
        <button onClick={send} disabled={(!text.trim() && !pendingImage) || sending}
          className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 disabled:opacity-40 hover:bg-blue-700 transition-colors mb-0.5">
          <Send size={18} className="text-white" />
        </button>
      </div>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
    </div>
  )
}
